import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FilterOperator, DatasetFieldInfo } from "@forgepoint/shared";
import { Trash2, Plus, Filter } from "lucide-react";
import { useDatasetExplorerStore } from "../datasetExplorerStore";
import EnumMultiSelect from "./EnumMultiSelect";

const FILTER_OPERATORS: FilterOperator[] = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "not_in",
  "between",
  "is_null",
  "is_not_null",
];

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: "=",
  neq: "\u2260",
  gt: ">",
  gte: "\u2265",
  lt: "<",
  lte: "\u2264",
  in: "in",
  not_in: "not in",
  between: "between",
  is_null: "is null",
  is_not_null: "is not null",
};

const NUMERIC_TYPES = new Set(["decimal", "integer", "real"]);

/**
 * Dynamic filter row builder for dataset queries.
 * Supports all filter operators with type-aware value inputs.
 */
export default function FilterBuilder() {
  const schemas = useDatasetExplorerStore((s) => s.schemas);
  const selectedDataset = useDatasetExplorerStore((s) => s.selectedDataset);
  const enabledJoins = useDatasetExplorerStore((s) => s.enabledJoins);
  const filters = useDatasetExplorerStore((s) => s.filters);
  const addFilter = useDatasetExplorerStore((s) => s.addFilter);
  const removeFilter = useDatasetExplorerStore((s) => s.removeFilter);
  const updateFilter = useDatasetExplorerStore((s) => s.updateFilter);

  const currentSchema = schemas.find((s) => s.dataset === selectedDataset);
  if (!currentSchema) {
    return null;
  }

  const availablePrefixes = [
    currentSchema.rootPrefix,
    ...Array.from(enabledJoins),
  ];

  const filterableFields = currentSchema.fields.filter(
    (f) =>
      f.filterable &&
      availablePrefixes.some((p) => f.field.startsWith(`${p}.`)),
  );

  /**
   * @param fieldRef - The field reference to look up
   * @returns The field info from the schema
   */
  function getFieldInfo(fieldRef: string): DatasetFieldInfo | undefined {
    return currentSchema?.fields.find((f) => f.field === fieldRef);
  }

  /**
   * Coerces a raw string value to the appropriate type based on the field schema.
   * @param raw - The raw input string
   * @param fieldRef - The field reference to determine type
   * @returns The coerced value
   */
  function coerceValue(raw: string, fieldRef: string): string | number {
    const info = getFieldInfo(fieldRef);
    if (info && NUMERIC_TYPES.has(info.type)) {
      const num = Number(raw);
      return Number.isNaN(num) ? raw : num;
    }
    return raw;
  }

  /**
   * Parses a comma-separated string into an array of coerced values.
   * @param raw - The comma-separated input
   * @param fieldRef - The field reference for type coercion
   * @returns Array of coerced values
   */
  function parseArrayValue(
    raw: string,
    fieldRef: string,
  ): Array<string | number> {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => coerceValue(s, fieldRef));
  }

  /**
   * Renders the appropriate value input for a filter row based on operator and field type.
   * Enum fields get select dropdowns; non-enum fields get text inputs.
   * @param filter - The current filter state
   * @param index - The filter row index
   * @returns The value input JSX element
   */
  function renderValueInput(
    filter: { field: string; op: FilterOperator; value?: unknown },
    index: number,
  ) {
    const fieldInfo = getFieldInfo(filter.field);
    const enumValues = fieldInfo?.enumValues;
    const isArray = filter.op === "in" || filter.op === "not_in";
    const isRange = filter.op === "between";

    if (isRange) {
      return (
        <>
          <Input
            className="h-7 text-xs flex-1"
            placeholder="From"
            value={
              Array.isArray(filter.value) ? String(filter.value[0] ?? "") : ""
            }
            onChange={(e) => {
              const from = coerceValue(e.target.value, filter.field);
              const to = Array.isArray(filter.value)
                ? filter.value[1]
                : undefined;
              updateFilter(index, {
                ...filter,
                value: [from, ...(to !== undefined ? [to] : [])] as Array<
                  string | number
                >,
              });
            }}
          />
          <Input
            className="h-7 text-xs flex-1"
            placeholder="To"
            value={
              Array.isArray(filter.value) ? String(filter.value[1] ?? "") : ""
            }
            onChange={(e) => {
              const to = coerceValue(e.target.value, filter.field);
              const from = Array.isArray(filter.value)
                ? filter.value[0]
                : undefined;
              updateFilter(index, {
                ...filter,
                value: [...(from !== undefined ? [from] : []), to] as Array<
                  string | number
                >,
              });
            }}
          />
        </>
      );
    }

    if (enumValues && isArray) {
      const selected = Array.isArray(filter.value)
        ? filter.value.map(String)
        : [];
      return (
        <EnumMultiSelect
          enumValues={enumValues}
          selected={selected}
          onChange={(values) =>
            updateFilter(index, { ...filter, value: values })
          }
        />
      );
    }

    if (enumValues && !isArray) {
      return (
        <Select
          value={
            filter.value !== null &&
            filter.value !== undefined &&
            !Array.isArray(filter.value)
              ? String(filter.value)
              : ""
          }
          onValueChange={(val) =>
            updateFilter(index, { ...filter, value: val })
          }
        >
          <SelectTrigger className="h-7 text-xs flex-1">
            <SelectValue placeholder="Select value" />
          </SelectTrigger>
          <SelectContent>
            {enumValues.map((val) => (
              <SelectItem key={val} value={val}>
                {val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (isArray) {
      return (
        <Input
          className="h-7 text-xs flex-1"
          placeholder="Comma-separated values"
          value={Array.isArray(filter.value) ? filter.value.join(", ") : ""}
          onChange={(e) =>
            updateFilter(index, {
              ...filter,
              value: parseArrayValue(e.target.value, filter.field),
            })
          }
        />
      );
    }

    return (
      <Input
        className="h-7 text-xs flex-1"
        placeholder="Value"
        value={
          filter.value !== null &&
          filter.value !== undefined &&
          !Array.isArray(filter.value)
            ? String(filter.value)
            : ""
        }
        onChange={(e) =>
          updateFilter(index, {
            ...filter,
            value: coerceValue(e.target.value, filter.field),
          })
        }
      />
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Filter className="h-3 w-3" />
        Filters
        {filters.length > 0 && (
          <Badge
            variant="secondary"
            className="ml-auto text-[10px] px-1.5 py-0 h-4 font-normal"
          >
            {filters.length}
          </Badge>
        )}
      </Label>
      <div className="space-y-2">
        {filters.map((filter, index) => {
          const op = filter.op;
          const needsNoValue = op === "is_null" || op === "is_not_null";

          return (
            <div
              key={index}
              className="flex flex-col gap-1.5 rounded-md border border-border/60 bg-background p-2"
            >
              <Select
                value={filter.field}
                onValueChange={(field) =>
                  updateFilter(index, { ...filter, field, value: undefined })
                }
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent>
                  {filterableFields.map((f) => (
                    <SelectItem key={f.field} value={f.field}>
                      {f.field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-1">
                <Select
                  value={filter.op}
                  onValueChange={(op) =>
                    updateFilter(index, {
                      ...filter,
                      op: op as FilterOperator,
                      value: undefined,
                    })
                  }
                >
                  <SelectTrigger className="h-7 text-xs flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_OPERATORS.map((op) => (
                      <SelectItem key={op} value={op}>
                        {OPERATOR_LABELS[op]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeFilter(index)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>

              {!needsNoValue && (
                <div className="flex gap-1">
                  {renderValueInput(filter, index)}
                </div>
              )}
            </div>
          );
        })}

        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() =>
            addFilter({
              field: filterableFields[0]?.field ?? "",
              op: "eq",
              value: "",
            })
          }
          disabled={filterableFields.length === 0}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Filter
        </Button>
      </div>
    </div>
  );
}
